import React from "react";

/**
 * Logo concept library for Cleveland Creative Group.
 * Each component renders just the mark (no wordmark) on a transparent background.
 * All marks share: slate/white primary + cyan (#22D3EE) accent.
 */

const CYAN = "#22D3EE";
const CYAN_DEEP = "#06B6D4";
const WHITE = "#F8FAFC";
const SLATE = "#475569";

/* 01 — Aperture
   Four rotated quarter-strokes forming an opening "eye" shape.
   Non-letter symbol. Reads as precision + optics + focus. */
export const LogoAperture = ({ size = 56 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} fill="none" xmlns="http://www.w3.org/2000/svg">
    <g strokeLinecap="round" strokeWidth="4" fill="none">
      <path d="M32 8 A24 24 0 0 1 56 32" stroke={WHITE} />
      <path d="M56 32 A24 24 0 0 1 32 56" stroke={WHITE} />
      <path d="M32 56 A24 24 0 0 1 8 32" stroke={SLATE} />
      <path d="M8 32 A24 24 0 0 1 32 8" stroke={SLATE} />
    </g>
    <circle cx="32" cy="32" r="5" fill={CYAN} />
  </svg>
);

/* 02 — Bolt
   A pure geometric C with a diagonal cyan slice through the top-right corner.
   Confident, architectural, single motion. */
export const LogoBolt = ({ size = 56 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M52 16 H28 A12 12 0 0 0 16 28 V36 A12 12 0 0 0 28 48 H52"
      stroke={WHITE}
      strokeWidth="5.5"
      strokeLinecap="round"
      fill="none"
    />
    <path d="M46 10 L58 22" stroke={CYAN} strokeWidth="5.5" strokeLinecap="round" />
  </svg>
);

/* 03 — Stack
   Three horizontal bars, shortest-to-longest, with the middle bar cyan.
   Abstract, scalable, speaks to "layered systems". */
export const LogoStack = ({ size = 56 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="18" y="14" width="22" height="8" rx="2" fill={WHITE} />
    <rect x="12" y="28" width="40" height="8" rx="2" fill={CYAN} />
    <rect x="22" y="42" width="30" height="8" rx="2" fill={SLATE} />
  </svg>
);

/* 04 — Marque (pure type)
   Custom wordmark-as-mark: "ccg." in heavy lowercase with cyan period.
   Editorial, literary, confident. No icon. */
export const LogoMarque = ({ size = 56 }) => (
  <svg viewBox="0 0 96 64" width={size * 1.5} height={size} fill="none" xmlns="http://www.w3.org/2000/svg">
    <text
      x="4"
      y="50"
      fill={WHITE}
      style={{
        fontFamily: "Outfit, sans-serif",
        fontWeight: 900,
        fontSize: "54px",
        letterSpacing: "-3px",
      }}
    >
      ccg
    </text>
    <circle cx="84" cy="48" r="4.5" fill={CYAN} />
  </svg>
);

/* 05 — Node
   Hexagon outline with a cyan node at one vertex. Pure symbol — evokes
   networks, nodes, distributed systems, blueprints. */
export const LogoNode = ({ size = 56 }) => {
  const points = [
    [32, 6],
    [54, 19],
    [54, 45],
    [32, 58],
    [10, 45],
    [10, 19],
  ];
  const pathD =
    "M" +
    points.map(([x, y]) => `${x} ${y}`).join(" L ") +
    " Z";
  return (
    <svg viewBox="0 0 64 64" width={size} height={size} fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d={pathD} stroke={WHITE} strokeWidth="3" strokeLinejoin="round" />
      <line x1="32" y1="6" x2="32" y2="32" stroke={SLATE} strokeWidth="2" />
      <line x1="54" y1="19" x2="32" y2="32" stroke={SLATE} strokeWidth="2" />
      <line x1="10" y1="45" x2="32" y2="32" stroke={SLATE} strokeWidth="2" />
      <circle cx="32" cy="32" r="4.5" fill={CYAN} />
      <circle cx="54" cy="19" r="3.5" fill={WHITE} />
    </svg>
  );
};

/* 06 — Trace
   A C drawn as a single continuous line that terminates in a cyan
   circuit-node dot. Reads as engineering / dev / data flow. */
export const LogoTrace = ({ size = 56 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M52 18 H28 A14 14 0 0 0 14 32 A14 14 0 0 0 28 46 H52"
      stroke={WHITE}
      strokeWidth="4.5"
      strokeLinecap="round"
      fill="none"
    />
    <circle cx="52" cy="18" r="5" fill={CYAN} />
    <circle cx="52" cy="18" r="2" fill="#020617" />
  </svg>
);

/* 07 — Prism (bonus)
   3D cube/prism with one face highlighted cyan. Engineering/architecture. */
export const LogoPrism = ({ size = 56 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* back/top face cyan */}
    <path d="M32 10 L52 20 L32 30 L12 20 Z" fill={CYAN} opacity="0.9" />
    {/* right face */}
    <path d="M52 20 L52 44 L32 54 L32 30 Z" fill={WHITE} />
    {/* left face */}
    <path d="M12 20 L12 44 L32 54 L32 30 Z" fill={SLATE} />
    {/* edges */}
    <path
      d="M32 10 L52 20 L32 30 L12 20 Z M52 20 L52 44 L32 54 L12 44 L12 20 M32 30 L32 54"
      stroke="#020617"
      strokeWidth="1"
      fill="none"
    />
  </svg>
);

/* 08 — Chevron (bonus)
   Three nested left-pointing chevrons — reads as `<<<` / code / iteration. */
export const LogoChevron = ({ size = 56 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M40 14 L22 32 L40 50" stroke={WHITE} strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M48 14 L30 32 L48 50" stroke={SLATE} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="22" cy="32" r="3.5" fill={CYAN} />
  </svg>
);

/* Registry for easy iteration */
export const LOGO_OPTIONS = [
  {
    id: "aperture",
    name: "Aperture",
    tagline: "Precision · focus",
    description:
      "An abstract opening formed by four arcs around a cyan core. Non-letter mark — works as a versatile brand symbol anywhere a letterform would feel boxed-in.",
    Component: LogoAperture,
  },
  {
    id: "bolt",
    name: "Bolt",
    tagline: "Architectural · sharp",
    description:
      "A clean geometric C sliced by a single diagonal cyan stroke. One confident gesture. Reads instantly at any size.",
    Component: LogoBolt,
  },
  {
    id: "stack",
    name: "Stack",
    tagline: "Modular · scalable",
    description:
      "Three bars of varying widths — the cyan center bar anchors the composition. Abstract and timeless; implies layered systems built to scale.",
    Component: LogoStack,
  },
  {
    id: "marque",
    name: "Marque",
    tagline: "Editorial · typographic",
    description:
      "A pure wordmark: lowercase ccg. with a single cyan period. No icon. Letter-forward and confident — favored by premium agencies.",
    Component: LogoMarque,
  },
  {
    id: "node",
    name: "Node",
    tagline: "Connected · networked",
    description:
      "A hexagonal container with internal edges converging on a cyan center-node. Evokes distributed systems, blueprints, and interconnection.",
    Component: LogoNode,
  },
  {
    id: "trace",
    name: "Trace",
    tagline: "Engineering · signal",
    description:
      "A C drawn as one continuous line that terminates in a cyan circuit node. Reads as data flow and engineered intent.",
    Component: LogoTrace,
  },
  {
    id: "prism",
    name: "Prism",
    tagline: "Dimensional · built",
    description:
      "An isometric cube with its top face in cyan. Suggests 3D engineering, architecture, and something physically constructed.",
    Component: LogoPrism,
  },
  {
    id: "chevron",
    name: "Chevron",
    tagline: "Code · iteration",
    description:
      "Two nested `<` chevrons pointing into a cyan pivot point. Reads as code brackets, velocity, and iterative build.",
    Component: LogoChevron,
  },
];
